import React from "react";
import { useLocation } from "react-router-dom";
import { X, Download, Share2, Sparkles } from "lucide-react";

/**
 * PWA Install Pill
 *
 * A compact, sticky "Install app" pill anchored to the bottom-right of the
 * landing page ("/"). Designed to bump conversion vs. a passive full-width
 * banner.
 *
 * Rules:
 * - Never shown once the app is running in standalone mode (i.e. installed).
 * - Never shown after `appinstalled` fires — a permanent flag is set so the
 *   pill will not resurface even if the OS uninstall-then-reinstall path is
 *   used (we clear the flag if `beforeinstallprompt` fires again, which only
 *   happens when the app is uninstalled).
 * - If the user dismisses (X), the pill re-appears after 7 days.
 * - Uses `navigator.getInstalledRelatedApps()` on browsers that support it
 *   (Chrome/Edge desktop + Android) as a second-line "already installed" check.
 * - iOS Safari has no `beforeinstallprompt`; we show an "Add to Home Screen"
 *   hint via a popover instead.
 * - Landing-only: hidden on `/dashboard`, auth pages, and every workspace
 *   route — the primary aim is to convert first-visit visitors.
 */

const DISMISS_KEY = "socibaba.pwa.install.dismissedAt";
const INSTALLED_KEY = "socibaba.pwa.install.installed";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  if (window.matchMedia && window.matchMedia("(display-mode: minimal-ui)").matches) return true;
  // iOS
  // @ts-expect-error non-standard
  if (window.navigator && window.navigator.standalone) return true;
  return false;
}

function isIos(): boolean {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const isIosDevice = /iPad|iPhone|iPod/.test(ua) && !("MSStream" in window);
  const isIpadOs =
    ua.includes("Macintosh") &&
    typeof navigator !== "undefined" &&
    ((navigator as unknown as { maxTouchPoints?: number }).maxTouchPoints ?? 0) > 1;
  return isIosDevice || isIpadOs;
}

function recentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function markInstalled() {
  try {
    localStorage.setItem(INSTALLED_KEY, "1");
    localStorage.removeItem(DISMISS_KEY);
  } catch {
    /* ignore */
  }
}

function isMarkedInstalled(): boolean {
  try {
    return localStorage.getItem(INSTALLED_KEY) === "1";
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

async function hasRelatedInstalledApp(): Promise<boolean> {
  try {
    const nav = navigator as unknown as {
      getInstalledRelatedApps?: () => Promise<Array<{ platform: string; url?: string; id?: string }>>;
    };
    if (typeof nav.getInstalledRelatedApps !== "function") return false;
    const apps = await nav.getInstalledRelatedApps();
    return Array.isArray(apps) && apps.length > 0;
  } catch {
    return false;
  }
}

export const InstallPrompt: React.FC = () => {
  const location = useLocation();
  const onLanding = location.pathname === "/";

  const [deferred, setDeferred] = React.useState<BeforeInstallPromptEvent | null>(null);
  const [ready, setReady] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const [iosMode, setIosMode] = React.useState(false);

  React.useEffect(() => {
    if (!onLanding) return;
    if (isStandalone() || isMarkedInstalled()) return;
    if (recentlyDismissed()) return;

    let cancelled = false;

    // Second-line check via getInstalledRelatedApps (Chromium desktop + Android)
    hasRelatedInstalledApp().then((installed) => {
      if (installed) {
        markInstalled();
        return;
      }
      if (cancelled) return;

      const onBeforeInstall = (e: Event) => {
        e.preventDefault();
        // If pill was dismissed and 7-day window not elapsed, still suppress
        if (recentlyDismissed() || isMarkedInstalled() || isStandalone()) return;
        setDeferred(e as BeforeInstallPromptEvent);
        setReady(true);
      };
      window.addEventListener("beforeinstallprompt", onBeforeInstall as EventListener);

      // iOS fallback (no beforeinstallprompt)
      let iosTimer: number | undefined;
      if (isIos()) {
        iosTimer = window.setTimeout(() => {
          if (!isStandalone() && !recentlyDismissed() && !isMarkedInstalled()) {
            setIosMode(true);
            setReady(true);
          }
        }, 3500);
      }

      const onInstalled = () => {
        markInstalled();
        setReady(false);
        setDeferred(null);
        setExpanded(false);
      };
      window.addEventListener("appinstalled", onInstalled);

      // Detect display-mode transitions (e.g. user opens installed app)
      const mm = window.matchMedia("(display-mode: standalone)");
      const onDisplayChange = () => {
        if (mm.matches) {
          markInstalled();
          setReady(false);
        }
      };
      try {
        mm.addEventListener("change", onDisplayChange);
      } catch {
        // Older Safari
        mm.addListener(onDisplayChange);
      }

      return () => {
        window.removeEventListener("beforeinstallprompt", onBeforeInstall as EventListener);
        window.removeEventListener("appinstalled", onInstalled);
        if (iosTimer) window.clearTimeout(iosTimer);
        try {
          mm.removeEventListener("change", onDisplayChange);
        } catch {
          mm.removeListener(onDisplayChange);
        }
      };
    });

    return () => {
      cancelled = true;
    };
  }, [onLanding]);

  const dismiss = React.useCallback(() => {
    markDismissed();
    setReady(false);
    setExpanded(false);
  }, []);

  const install = React.useCallback(async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") {
        markInstalled();
        setReady(false);
      } else {
        // User declined the native prompt — respect the 7-day cool-off
        dismiss();
      }
    } catch {
      dismiss();
    } finally {
      setDeferred(null);
    }
  }, [deferred, dismiss]);

  if (!onLanding || !ready) return null;

  return (
    <div
      role="region"
      aria-label="Install SociBaba"
      data-testid="pwa-install-pill"
      className="fixed z-[60] right-3 sm:right-6"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)" }}
    >
      {expanded ? (
        <div
          data-testid="pwa-install-pill-expanded"
          className="w-[280px] rounded-2xl border border-[color:var(--sb-border,#e2e8f0)] bg-[color:var(--sb-surface,#fff)]/95 shadow-xl backdrop-blur-md p-3"
        >
          <div className="flex items-start gap-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#F97316] to-[#0A1128] overflow-hidden">
              <img src="/icon-192.png" alt="" loading="lazy" decoding="async" className="h-9 w-9" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-[color:var(--sb-text,#0A1128)]">
                Install SociBaba
              </div>
              {iosMode ? (
                <p className="mt-0.5 text-[11px] leading-snug text-[color:var(--sb-text-muted,#475569)]">
                  Tap <Share2 className="inline h-3 w-3 align-[-2px]" /> Share →{" "}
                  <b className="text-[color:var(--sb-text,#0A1128)]">Add to Home Screen</b>.
                </p>
              ) : (
                <p className="mt-0.5 text-[11px] leading-snug text-[color:var(--sb-text-muted,#475569)]">
                  Launch it full-screen. Offline-ready. One tap from your home screen.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Not now"
              data-testid="pwa-install-dismiss"
              className="shrink-0 rounded-full p-1 text-[color:var(--sb-text-muted,#475569)] hover:bg-[color:var(--sg-muted-bg,#f1f5f9)] hover:text-[color:var(--sb-text,#0A1128)]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {!iosMode && deferred && (
            <button
              type="button"
              onClick={install}
              data-testid="pwa-install-btn"
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-[color:var(--sb-saffron,#F97316)] hover:bg-[color:var(--sb-saffron-hover,#EA580C)] px-3 py-2 text-xs font-semibold text-white transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Install app
            </button>
          )}
          {iosMode && (
            <button
              type="button"
              onClick={dismiss}
              data-testid="pwa-install-btn"
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-[color:var(--sb-navy,#0A1128)] hover:opacity-90 px-3 py-2 text-xs font-semibold text-[color:var(--sb-cream,#F4F1EA)] transition-opacity"
            >
              Got it
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          data-testid="pwa-install-pill-btn"
          aria-label="Install SociBaba app"
          className="group inline-flex items-center gap-2 rounded-full border border-[color:var(--sb-border,#e2e8f0)] bg-[color:var(--sb-surface,#fff)]/95 shadow-lg backdrop-blur-md pl-1.5 pr-4 py-1.5 text-xs font-semibold text-[color:var(--sb-text,#0A1128)] hover:shadow-xl hover:-translate-y-0.5 transition-all"
        >
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[color:var(--sb-saffron,#F97316)] text-white group-hover:scale-110 transition-transform">
            {iosMode ? (
              <Sparkles className="h-3.5 w-3.5" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
          </span>
          <span>Install app</span>
        </button>
      )}
    </div>
  );
};

export default InstallPrompt;
